import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-text-mask';
import {injectIntl, intlShape, FormattedMessage} from 'react-intl';

import ValidationMessage from './ValidationMessage';
import Group from './Group';
import {changeFormat, getDateFormat} from '../../utils/date';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

const STATUS = {
  INITIAL: 'init',
  INPUT: 'input',
  DONE: 'done'
};

const InfoSection = ({children, highlighted}) => (
  <span className={`info__section ${highlighted ? 'info__section--highlighted' : ''}`}>
    {children}
  </span>
);

InfoSection.propTypes = {
  children: PropTypes.node.isRequired,
  highlighted: PropTypes.bool.isRequired
};

const getLabelFormat = (position, messages) => ({
  day: <InfoSection highlighted={position === 'D'}>{messages['date.day']}</InfoSection>,
  month: <InfoSection highlighted={position === 'M'}>{messages['date.month']}</InfoSection>,
  year: <InfoSection highlighted={position === 'Y'}>{messages['date.year']}</InfoSection>
});

class DateInput extends PureComponent {
  constructor(props) {
    super(props);

    this.FORMAT = getDateFormat(props.lang);
    this.MASK = this.FORMAT.split('').map(char => (char.match(/[a-zA-Z]/) ? /\d/ : char));

    this.state = {
      ...this.getDateState(props.input.value),
      position: this.FORMAT[0],
      status: STATUS.INITIAL
    };

    this.onDateChange = this.onDateChange.bind(this);
    this.onDateBlur = this.onDateBlur.bind(this);
    this.onDateFocus = this.onDateFocus.bind(this);
    this.handleCaretChange = this.handleCaretChange.bind(this);
  }

  getDateState(value) {
    const dateString = changeFormat(this.FORMAT, ISO_DATE_FORMAT, value);
    const isValid = typeof new Date(dateString) !== 'string';

    return {
      dateString,
      isValid
    };
  }

  setCaretPosition(position) {
    let clampedPosition;

    if (position < 0) {
      clampedPosition = 0;
    } else if (position > this.FORMAT.length - 1) {
      clampedPosition = this.FORMAT.length - 1;
    } else {
      clampedPosition = position;
    }

    const formatChar = this.FORMAT[clampedPosition];

    this.setState({
      position: formatChar.match(/[a-zA-Z]/) ? formatChar : this.FORMAT[clampedPosition - 1]
    });
  }

  onDateChange(e) {
    const value = e.target.value || null;
    if (value && value.match(/_/)) {
      this.setState({status: STATUS.INPUT});
    } else {
      this.setState({status: STATUS.DONE});
    }
    this.setState(this.getDateState(value));
    this.props.input.onChange(changeFormat(this.FORMAT, ISO_DATE_FORMAT, value));
  }

  onDateFocus(e) {
    this.props.input.onFocus(e);
    this.setState({status: STATUS.INPUT});
  }

  onDateBlur() {
    this.props.input.onBlur(this.props.input.value);
    this.setState({status: STATUS.DONE});
  }

  handleCaretChange(e) {
    const {target} = e;
    setTimeout(() => this.setCaretPosition(target.selectionEnd), 0); // Fix caret positioning on Android
  }

  render() {
    const {meta, input, intl, ...rest} = this.props;
    const hasError = meta.touched && meta.error;
    const showErrorLabel = meta.error && this.state.status === STATUS.DONE;

    const inner = (
      <FormattedMessage
        id="__date_format" // its required, but we generate label programmatically
        values={{
          ...getLabelFormat(this.state.position, intl.messages)
        }}
      />
    );

    return (
      <Group className="dateinput">
        <div className="input-container">
          <div className="input-icon icon-calendar" />
          <MaskedInput
            {...rest}
            {...input}
            mask={this.MASK}
            onChange={this.onDateChange}
            onBlur={this.onDateBlur}
            onFocus={this.onDateFocus}
            onKeyUp={this.handleCaretChange}
            onTouchStart={this.handleCaretChange}
            onKeyDown={this.handleCaretChange}
            onTouchEnd={this.handleCaretChange}
            onClick={this.handleCaretChange}
            value={changeFormat(ISO_DATE_FORMAT, this.FORMAT, this.props.input.value)}
            keepCharPositions={true}
            placeholder={this.props.label}
            className={`form-input ${hasError ? 'has-error' : ''}`}
          />
        </div>
        {!showErrorLabel &&
          meta.active && (
            <span className="info">
              <FormattedMessage
                id="form.bday.label"
                values={{
                  format: inner
                }}
              />
            </span>
          )}
        {showErrorLabel && <ValidationMessage>{meta.error}</ValidationMessage>}
      </Group>
    );
  }
}

DateInput.defaultProps = {
  lang: 'en'
};

DateInput.propTypes = {
  intl: intlShape,
  lang: PropTypes.string,
  label: PropTypes.string.isRequired,
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool.isRequired,
    error: PropTypes.any
  }).isRequired
};

export default injectIntl(DateInput);



// WEBPACK FOOTER //
// ./components/Form/DateInput.jsx